/**
 * bot-test-chat.tsx — Khung "Chat thử" gọn cho một nhân viên AI (dùng ở luồng tạo nhân viên AI; dùng lại được ở nơi khác).
 *
 * Chạy đúng pipeline thật qua sandbox (`/ai/simulate/conversation` + `/ai/simulate/reply`, hàm ở use-ai-train-test.ts):
 * bong bóng trả lời, nguồn kiến thức đã trích (tên tài liệu · kho · điểm), "AI đã tra", chuyển người; mỗi câu trả lời có
 * 👍 / 👎 — 👎 mở gợi ý mục cần sửa và ô "Câu trả lời đúng" lưu thành hỏi đáp (`/ai/simulate/feedback`).
 *
 * Lượt thử do trang cha giữ (`turns` + `onTurnsChange`) để tính trạng thái bước; trang cha gửi câu gợi ý qua ref `send()`.
 * "Khách giả lập" sao chép tối thiểu từ `pages/ai/train/test-panel.tsx` (ContactPicker không được export ở đó).
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { toast } from 'sonner'
import { Check, ChevronDown, Loader2, RotateCcw, SendHorizonal, ThumbsDown, ThumbsUp, UserRound, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/misc'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { useAiDataSources } from '@/hooks/use-ai'
import { useContacts } from '@/hooks/use-contacts'
import { createTestConversation, saveTurnFeedback, simulateTurn } from '@/hooks/use-ai-train-test'
import { traceSourceSummary } from '@/pages/ai/data-sources-lib'

export interface ChatSource {
  title: string
  scorePct: number | null
  knowledgeBaseName?: string | null
  type: string
}

export interface ChatTurn {
  key: string
  customerText: string
  bubbles: string[]
  reply: string | null
  runId?: string | null
  handoffReason?: string | null
  handoffNotice?: string | null
  sources: ChatSource[]
  looked?: string | null
  rating?: 'up' | 'down' | null
  /** Đã lưu câu trả lời đúng thành hỏi đáp. */
  savedFaq?: boolean
  error?: string | null
}

export interface DislikeHintLink {
  key: string
  label: string
  desc: string
  onClick: () => void
}

export interface BotTestChatHandle {
  send: (text: string) => void
}

export interface BotTestChatProps {
  botId: string
  botName: string
  botEmoji: string
  turns: ChatTurn[]
  onTurnsChange: (next: ChatTurn[] | ((prev: ChatTurn[]) => ChatTurn[])) => void
  /** Gợi ý mục cần sửa khi 👎 (trang cha quyết định link). */
  hintsFor?: (turn: ChatTurn) => DislikeHintLink[]
  /** Kho nhận hỏi đáp khi lưu câu trả lời đúng (trống → máy chủ chọn kho bot đang tra / "Kho mặc định"). */
  knowledgeBaseId?: string | null
  className?: string
}

let seq = 0

export const BotTestChat = forwardRef<BotTestChatHandle, BotTestChatProps>(function BotTestChat(
  { botId, botName, botEmoji, turns, onTurnsChange, hintsFor, knowledgeBaseId, className },
  ref,
) {
  const { t } = useTranslation('aiComp')
  const [pending, setPending] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [contact, setContact] = useState<{ id: string; name: string } | null>(null)
  const convRef = useRef<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const busyRef = useRef(false)
  const { data: dsData } = useAiDataSources()
  const labelOf = (k: string) => dsData?.sources.find((s) => s.key === k)?.label ?? k

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }) }, [turns.length, pending])
  // Đổi nhân viên AI → phiên mới.
  useEffect(() => { convRef.current = null }, [botId])

  async function send(raw: string) {
    const text = raw.trim()
    if (!text || busyRef.current) return
    busyRef.current = true
    setInput('')
    setPending(text)
    try {
      if (!convRef.current) convRef.current = (await createTestConversation(`Chạy thử — ${botName}`)).conversationId // i18n-ignore: tên phiên lưu ở máy chủ
      const res = await simulateTurn({ conversationId: convRef.current, customerText: text, botId, contactId: contact?.id })
      const turn: ChatTurn = {
        key: `w${Date.now()}-${++seq}`,
        customerText: text,
        reply: res.reply,
        bubbles: res.bubbles ?? (res.reply ? [res.reply] : []),
        runId: res.runId ?? null,
        handoffReason: res.handoff?.should ? (res.handoff.reason || t('botTest.handoffDefault')) : null,
        handoffNotice: res.handoff?.should ? (res.handoffNotice ?? null) : null,
        sources: (res.detail?.sources ?? []).map((s) => ({ title: s.title, scorePct: s.scorePct, knowledgeBaseName: s.knowledgeBaseName ?? null, type: s.type })),
        looked: traceSourceSummary(res.trace, labelOf),
        rating: null,
      }
      onTurnsChange((prev) => [...prev, turn])
    } catch (err) {
      onTurnsChange((prev) => [...prev, { key: `e${Date.now()}-${++seq}`, customerText: text, bubbles: [], reply: null, sources: [], error: apiError(err) }])
    } finally {
      busyRef.current = false
      setPending(null)
    }
  }

  useImperativeHandle(ref, () => ({ send: (text: string) => void send(text) }))

  function rate(key: string, rating: 'up' | 'down') {
    onTurnsChange((prev) => prev.map((x) => (x.key === key ? { ...x, rating: x.rating === rating ? null : rating } : x)))
  }

  function resetSession() {
    if (pending) return
    convRef.current = null
    onTurnsChange([])
  }

  const busy = pending !== null

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div className="mb-2 flex items-center gap-2">
        <ContactPicker value={contact} onChange={setContact} disabled={busy} />
        <Button variant="ghost" size="sm" className="ml-auto h-8 gap-1" onClick={resetSession} disabled={busy || turns.length === 0} title={t('botTest.resetTitle')}>
          <RotateCcw className="h-3.5 w-3.5" /> {t('botTest.reset')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg border bg-muted/30 p-3" aria-live="polite">
        {turns.length === 0 && !busy && (
          <p className="py-10 text-center text-xs text-muted-foreground">
            {t('botTest.emptyIntro', { emoji: botEmoji, name: botName })}<br />
            {t('botTest.emptyNote')}
          </p>
        )}
        {turns.map((turn) => (
          <TurnView
            key={turn.key} turn={turn} botEmoji={botEmoji} onRate={(r) => rate(turn.key, r)}
            hints={turn.rating === 'down' && hintsFor ? hintsFor(turn) : []}
            botId={botId} knowledgeBaseId={knowledgeBaseId}
            onSaved={() => onTurnsChange((prev) => prev.map((x) => (x.key === turn.key ? { ...x, savedFaq: true } : x)))}
          />
        ))}
        {pending && (
          <div className="space-y-1.5">
            <div className="flex justify-end">
              <div className="max-w-[80%] whitespace-pre-wrap rounded-lg bg-primary/80 px-3 py-2 text-sm text-primary-foreground">{pending}</div>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('botTest.thinking', { emoji: botEmoji })}
            </p>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          placeholder={t('botTest.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(input) } }}
          disabled={busy}
          aria-label={t('botTest.messageAria')}
        />
        <Button onClick={() => void send(input)} disabled={!input.trim() || busy} aria-label={t('botTest.send')}><SendHorizonal className="h-4 w-4" /></Button>
      </div>
    </div>
  )
})

function TurnView({ turn, botEmoji, onRate, hints, botId, knowledgeBaseId, onSaved }: {
  turn: ChatTurn
  botEmoji: string
  onRate: (r: 'up' | 'down') => void
  hints: DislikeHintLink[]
  botId: string
  knowledgeBaseId?: string | null
  onSaved: () => void
}) {
  const { t } = useTranslation('aiComp')
  const [answer, setAnswer] = useState('')
  const [saving, setSaving] = useState(false)

  async function saveFaq() {
    if (!answer.trim()) return
    setSaving(true)
    try {
      const r = await saveTurnFeedback({
        runId: turn.runId ?? undefined, customerText: turn.customerText, correctAnswer: answer.trim(), saveAs: 'faq',
        originalReply: turn.reply, knowledgeBaseId: knowledgeBaseId || null, botId,
      })
      toast.success(r.knowledgeBaseName ? t('botTest.savedFaqIn', { kb: r.knowledgeBaseName }) : t('botTest.savedFaq'))
      setAnswer('')
      onSaved()
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex justify-end">
        <div className="max-w-[80%] whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">{turn.customerText}</div>
      </div>
      {turn.error ? (
        <p className="text-center text-xs italic text-destructive">{t('botTest.errorPrefix')} {turn.error}</p>
      ) : (
        <>
          {turn.bubbles.map((b, i) => (
            <div key={i} className="flex justify-start">
              <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border bg-background px-3 py-2 text-sm">
                {i === 0 && <span className="mr-1">{botEmoji}</span>}{b}
              </div>
            </div>
          ))}
          {turn.bubbles.length === 0 && (
            <>
              {turn.handoffNotice && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border bg-background px-3 py-2 text-sm">
                    <span className="mr-1">{botEmoji}</span>{turn.handoffNotice}
                  </div>
                </div>
              )}
              <p className="text-center text-xs italic text-muted-foreground">
                {turn.handoffReason ? t('botTest.handoff', { reason: turn.handoffReason }) : t('botTest.noReply')}
              </p>
            </>
          )}
          {turn.sources.length > 0 && (
            <div className="ml-1 rounded-md border bg-background/60 px-2 py-1.5">
              <p className="text-[11px] font-medium text-muted-foreground">{t('botTest.sourcesCited', { count: turn.sources.length })}</p>
              <ul className="mt-0.5 space-y-0.5">
                {turn.sources.slice(0, 3).map((s, i) => (
                  <li key={i} className="truncate text-[11px] text-muted-foreground">
                    📄 {s.title}{s.knowledgeBaseName ? ` · ${s.knowledgeBaseName}` : ''}{s.scorePct != null ? ` · ${s.scorePct}%` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1 pl-1">
            <button
              type="button" aria-label={t('botTest.good')} aria-pressed={turn.rating === 'up'}
              className={cn('rounded p-1 hover:bg-muted', turn.rating === 'up' ? 'text-success' : 'text-muted-foreground')}
              onClick={() => onRate('up')}
            ><ThumbsUp className="h-3.5 w-3.5" /></button>
            <button
              type="button" aria-label={t('botTest.bad')} aria-pressed={turn.rating === 'down'}
              className={cn('rounded p-1 hover:bg-muted', turn.rating === 'down' ? 'text-destructive' : 'text-muted-foreground')}
              onClick={() => onRate('down')}
            ><ThumbsDown className="h-3.5 w-3.5" /></button>
            {turn.sources.length === 0 && <span className="text-[11px] text-muted-foreground">{t('botTest.noSources')}</span>}
            {turn.looked && <span className="text-[11px] text-muted-foreground">{t('botTest.looked', { what: turn.looked })}</span>}
          </div>
          {turn.rating === 'down' && (
            <div className="ml-1 space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-2">
              <p className="text-xs font-medium">{t('botTest.badFix')}</p>
              <ul className="space-y-1">
                {hints.map((h) => (
                  <li key={h.key} className="text-xs">
                    <button type="button" className="font-medium text-primary hover:underline" onClick={h.onClick}>{h.label}</button>
                    <span className="text-muted-foreground"> — {h.desc}</span>
                  </li>
                ))}
              </ul>
              {turn.savedFaq ? (
                <p className="text-xs text-success">{t('botTest.savedCorrect')}</p>
              ) : (
                <div className="space-y-1.5">
                  <Textarea
                    className="min-h-16 text-xs" value={answer} onChange={(e) => setAnswer(e.target.value)}
                    placeholder={t('botTest.correctPlaceholder')} aria-label={t('botTest.correctAria')}
                  />
                  <div className="flex justify-end">
                    <Button size="sm" className="h-7" disabled={!answer.trim() || saving} onClick={() => void saveFaq()}>
                      {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {t('botTest.saveFaq')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** "Khách giả lập": chọn một khách thật trong CRM để AI dùng hồ sơ + trí nhớ của khách đó (chỉ đọc). */
function ContactPicker({ value, onChange, disabled }: {
  value: { id: string; name: string } | null
  onChange: (v: { id: string; name: string } | null) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('aiComp')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data, isFetching } = useContacts({ search: search.trim() || undefined, limit: 12, page: 1 })
  const list = (data?.contacts ?? []).filter((c) => c.source !== 'ai_sandbox').slice(0, 8)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 max-w-[65%] gap-1.5" disabled={disabled} title={t('botTest.contactTitle')}>
          <UserRound className="h-3.5 w-3.5" />
          <span className="truncate">{value ? t('botTest.contactNamed', { name: value.name }) : t('botTest.contact')}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <p className="mb-1.5 text-[11px] text-muted-foreground">
          {t('botTest.contactHint')}
        </p>
        <Input autoFocus className="h-8" placeholder={t('botTest.contactSearch')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="mt-1.5 max-h-56 space-y-0.5 overflow-y-auto">
          <button type="button" className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
            onClick={() => { onChange(null); setOpen(false) }}>
            {!value ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5 opacity-50" />} {t('botTest.anon')}
          </button>
          {isFetching && list.length === 0 && <p className="px-2 py-1 text-xs text-muted-foreground">{t('botTest.searching')}</p>}
          {list.map((c) => {
            const name = c.crmName || c.fullName || c.phone || t('botTest.customer')
            return (
              <button key={c.id} type="button"
                className={cn('flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted', value?.id === c.id && 'bg-primary/5')}
                onClick={() => { onChange({ id: c.id, name }); setOpen(false) }}>
                {value?.id === c.id ? <Check className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5 opacity-50" />}
                <span className="min-w-0 flex-1 truncate">{name}</span>
                {c.phone && <span className="text-[11px] text-muted-foreground">{c.phone}</span>}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
